import {
  backlogAnswerNote,
  findFactoryQuestions,
  formatFactoryQuestionsBlock,
  isSkippedAnswer,
  parseFactoryAnswers,
  parseQuestionsReply,
  type FactoryAnswer,
  type FactoryQuestion,
  type FactoryQuestions,
} from './factory-questions';
import { backlogItemRound, findFactoryBacklog, type BacklogOutcome } from './factory-backlog';
import { parseMentions } from './task-contexts';
import {
  backlogByAgent,
  findFactorySuggestions,
  formatFactorySuggestionsBlock,
  parseSuggestionDecisions,
  roundForDecisions,
  suggestionsMessageRef,
  type FactorySuggestion,
  type FactorySuggestions,
  type SuggestionDecision,
} from './factory-suggestions';

/**
 * T-58: the AI builder's questions or suggestions on a mirrored task, sent to the task's source as a
 * comment, so the people upstream decide them. The message that went up is the sender's chat message
 * on the task, marked with `payload.deferred` (DeferredMarker): which round and which items it carried,
 * and, once a reply comes back, Jev's proposed answer for each (UpstreamProposal).
 *
 * The rounds and their closing rule live here, so the Decisions card, the comments and the worker
 * agree on when a round is answered:
 * - a round with nothing sent closes on an Answers message or a reply in words (SF-T-163: the card's
 *   Send answers or Reply in words), on an @AI builder comment (it goes to the builder as the reply), or
 *   when the factory asks a newer round; a plain comment leaves it open;
 * - a round with items sent upstream closes only on an Answers message (a person's, or the factory's
 *   automatic one), on its suggestions being decided (Apply), or on someone removing the upstream wait.
 */

export const DEFERRED_KINDS = ['questions', 'suggestions'] as const;
export type DeferredKind = (typeof DEFERRED_KINDS)[number];

/** The choices a suggestion can get from upstream: Yes please, Backlog or No thanks. */
export const UPSTREAM_SUGGESTION_CHOICES = ['yes', 'backlog', 'no'] as const;
export type UpstreamSuggestionChoice = (typeof UPSTREAM_SUGGESTION_CHOICES)[number];

/** Jev's pick for one sent item, from the replies upstream. */
export interface UpstreamProposal {
  /** A question's option label, or a suggestion's action (yes, backlog, no); for a multi-select question, its labels joined with "; ". */
  choice: string;
  /** A multi-select question's picked option labels. */
  choices?: string[];
  /** 0 to 1. */
  confidence: number;
  /** The synced-in reply it came from (the newest reply when it was matched), and its author. */
  reply_event_id: string | null;
  reply_author: string | null;
}

/**
 * SF-T-170: how a source takes the AI builder's questions and suggestions: `text` (GitHub, Asana,
 * Trello: plain text people reply to in words, read by Jev) or `blocks` (jflo: the factory's own
 * blocks, shown as its cards, whose replies come back in the factory's own formats and are read exactly).
 */
export type DecisionFormat = 'blocks' | 'text';

/** SF-T-170: a reply in words to questions sent as blocks (jflo's Reply in words), and who wrote it. */
export interface UpstreamWords {
  text: string;
  reply_event_id: string | null;
  reply_author: string | null;
}

/** `payload.deferred` on the message that went upstream. */
export interface DeferredMarker {
  /** The factory message with the questions or suggestions block. */
  event_id: string;
  kind: DeferredKind;
  /** The items it carried, by their number in the round (1-based). */
  numbers: number[];
  /** SF-T-101: who the comment tagged on the source, by their account id and name there. */
  mention?: { id: string; name: string };
  /** SF-T-170: `blocks` when it went up as a factory block (numbered from 1 there), so replies are read exactly. Absent: plain text. */
  format?: DecisionFormat;
  /** SF-T-170: questions answered upstream in words (Reply to the questions), read exactly from the reply. */
  words?: UpstreamWords;
  /** Jev's picks so far, by item number. */
  proposals?: Record<string, UpstreamProposal>;
  /** The newest reply (its time, or its edit's) Jev has read for these items: a reply newer than this is matched again. */
  matched_through?: string;
  /** How many replies Jev read then: fewer now (one deleted upstream) is matched again too. */
  matched_replies?: number;
}

/** `payload.upstream_answers` on the factory's automatic Answers message: the round it answers. */
export interface UpstreamAnswersMarker {
  event_id: string;
}

/** What the rounds read from a task's events (TaskEvent, or the worker's rows). */
export interface RoundEvent {
  id: string;
  kind: string;
  body: string | null;
  actor_label: string | null;
  actor_user_id: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
  /** When it was last edited (a reply edited upstream), if it was. */
  edited_at?: string | null;
  /** SF-T-170: its comment's id on the source (a send that went up, a reply synced in), where the reader has it. */
  external_id?: string | null;
}

const isRecord = (v: unknown): v is Record<string, unknown> => Boolean(v) && typeof v === 'object' && !Array.isArray(v);

function readProposal(v: unknown): UpstreamProposal | null {
  if (!isRecord(v) || typeof v.choice !== 'string' || typeof v.confidence !== 'number') return null;
  return {
    choice: v.choice,
    ...(Array.isArray(v.choices) ? { choices: v.choices.filter((c): c is string => typeof c === 'string') } : {}),
    confidence: v.confidence,
    reply_event_id: typeof v.reply_event_id === 'string' ? v.reply_event_id : null,
    reply_author: typeof v.reply_author === 'string' ? v.reply_author : null,
  };
}

/** The marker on a message that went upstream; null for any other message. */
export function deferredMarker(payload: unknown): DeferredMarker | null {
  const d = isRecord(payload) ? payload.deferred : null;
  if (!isRecord(d) || typeof d.event_id !== 'string' || !(DEFERRED_KINDS as readonly unknown[]).includes(d.kind) || !Array.isArray(d.numbers)) return null;
  const numbers = d.numbers.map(Number).filter((n) => Number.isInteger(n) && n > 0);
  if (!numbers.length) return null;
  const proposals: Record<string, UpstreamProposal> = {};
  if (isRecord(d.proposals)) {
    for (const [n, p] of Object.entries(d.proposals)) {
      const read = readProposal(p);
      if (read) proposals[n] = read;
    }
  }
  return {
    event_id: d.event_id,
    kind: d.kind as DeferredKind,
    numbers,
    proposals,
    ...(d.format === 'blocks' ? { format: 'blocks' as const } : {}),
    ...(isRecord(d.words) && typeof d.words.text === 'string'
      ? {
          words: {
            text: d.words.text,
            reply_event_id: typeof d.words.reply_event_id === 'string' ? d.words.reply_event_id : null,
            reply_author: typeof d.words.reply_author === 'string' ? d.words.reply_author : null,
          },
        }
      : {}),
    ...(typeof d.matched_through === 'string' ? { matched_through: d.matched_through } : {}),
    ...(typeof d.matched_replies === 'number' ? { matched_replies: d.matched_replies } : {}),
  };
}

/** A message that sent items upstream, or tried to (`deferred_failed`: the post failed): never a reply to a round. */
export function isSendMessage(payload: unknown): boolean {
  return isRecord(payload) && (isRecord(payload.deferred) || isRecord(payload.deferred_failed));
}

/** The round the factory's automatic Answers message answers; null for any other message. */
export function upstreamAnswersMarker(payload: unknown): UpstreamAnswersMarker | null {
  const a = isRecord(payload) ? payload.upstream_answers : null;
  return isRecord(a) && typeof a.event_id === 'string' ? { event_id: a.event_id } : null;
}

/**
 * How an upstream wait ending changes the rounds: `removed` (someone took it off by hand) closes them;
 * `unmirrored` (the source was turned off or deleted, or the task is gone there) takes the items back
 * here, open as if never sent, since no reply can come. Any other end (a reply, the task finishing) none.
 */
function upstreamWaitEnded(event: RoundEvent): 'removed' | 'unmirrored' | null {
  if (event.kind !== 'waiting_removed' || !isRecord(event.payload)) return null;
  const waiting = event.payload.waiting;
  if (!isRecord(waiting) || waiting.target !== 'upstream') return null;
  return event.payload.reason === 'removed' ? 'removed' : event.payload.reason === 'unmirrored' ? 'unmirrored' : null;
}

/** The round a person's Answers message answers (`payload.answers_for`, set by Send answers on a card); null when it names none. */
export function answersForOf(payload: unknown): string | null {
  return isRecord(payload) && typeof payload.answers_for === 'string' ? payload.answers_for : null;
}

/** Jev has read a reply to this sending message (its items aren't just waiting for one). Takes any value: the cards pass what they have. */
export function sendReplyRead(message: unknown): boolean {
  return isRecord(message) && Boolean(deferredMarker(message.payload)?.matched_through);
}

/** Jev has read a reply for the round (it isn't just waiting for one): any of its sending messages was matched. */
export function roundReplyRead(round: { sent: ReadonlyMap<number, RoundEvent> }): boolean {
  return [...round.sent.values()].some((e) => Boolean(deferredMarker(e.payload)?.matched_through));
}

/* Questions ------------------------------------------------------------ */

/** One round of the AI builder's questions on a task (T-10), what came back (T-22), and what went upstream (T-58). */
export interface QuestionRound<E extends RoundEvent = RoundEvent> {
  /** The factory's message that asked. */
  event: E;
  questions: FactoryQuestions;
  /**
   * Closed: an Answers message or a reply in words came, or (nothing sent) an @AI builder comment or a
   * newer round, or (something sent) the upstream wait was removed.
   */
  answered: boolean;
  /** SF-T-279: closed unanswered, because a person closed the run that asked (answered is true too). */
  closed?: boolean;
  /** The Answers message (or reply in words, SF-T-163) for this round, if there is one. */
  reply: E | null;
  /** One answer per question from that message (null where none was given); null for a reply in words. */
  answers: (string | null)[] | null;
  /** SF-T-163: the reply's text, when it was a reply in words rather than an answer per question. */
  words: string | null;
  /** The questions sent upstream (by number, 1-based), each with the message that sent it. */
  sent: Map<number, E>;
  /** Jev's picks from the replies upstream, by question number. */
  proposals: Map<number, UpstreamProposal>;
  /** SF-T-170: the round answered upstream in words (a source that reads blocks), in place of a pick per question. */
  upstreamWords: UpstreamWords | null;
  /** SF-T-308: what the AI builder reported for the questions marked Backlog (factory-backlog), by number: the task, or why none. */
  backlog: Map<number, BacklogOutcome>;
}

/** SF-T-308: the questions in a round marked Backlog that the AI builder hasn't reported on yet, by number, with their text. */
export function unreportedBacklog(round: Pick<QuestionRound, 'answers' | 'backlog' | 'questions'>): Map<number, string> {
  const out = new Map<number, string>();
  round.answers?.forEach((a, i) => {
    if (backlogAnswerNote(a) !== null && !round.backlog.has(i + 1)) out.set(i + 1, round.questions.questions[i]?.question ?? '');
  });
  return out;
}

/** SF-T-279: the runs a `runs_closed` event closed (their numbers), or null for any other event. */
export function runsClosedOf(event: Pick<RoundEvent, 'kind' | 'payload'>): number[] | null {
  if (event.kind !== 'runs_closed' || !isRecord(event.payload) || !Array.isArray(event.payload.runs)) return null;
  return event.payload.runs.map(Number).filter(Number.isFinite);
}

/** The run a factory message came from, by its label ("Factory · R-12"); null for anything else. */
function askedByRun(label: string | null): number | null {
  const match = label ? /^Factory · R-(\d+)$/.exec(label) : null;
  return match ? Number(match[1]) : null;
}

/** Every round of questions in a task's events (oldest first), oldest round first. */
export function questionRounds<E extends RoundEvent>(events: E[]): QuestionRound<E>[] {
  const rounds: QuestionRound<E>[] = [];
  let newest: QuestionRound<E> | null = null;
  for (const event of events) {
    const ended = upstreamWaitEnded(event);
    if (ended) {
      for (const round of rounds) {
        if (round.answered || !round.sent.size) continue;
        if (ended === 'removed') round.answered = true;
        else {
          // No reply can come now: nothing is out, and what Jev read before isn't offered as the answer.
          round.sent.clear();
          round.proposals.clear();
          round.upstreamWords = null;
        }
      }
      continue;
    }
    // SF-T-279: a person closed the runs that asked: their open rounds close unanswered.
    const closedRuns = runsClosedOf(event);
    if (closedRuns) {
      for (const round of rounds) {
        const run = askedByRun(round.event.actor_label);
        if (!round.answered && run !== null && closedRuns.includes(run)) {
          round.answered = true;
          round.closed = true;
        }
      }
      continue;
    }
    if (event.kind !== 'chat' || !event.body) continue;
    const auto = upstreamAnswersMarker(event.payload);
    if (event.actor_label && !auto) {
      // SF-T-308: the AI builder's report on the questions marked Backlog: onto their rounds.
      for (const item of (findFactoryBacklog(event.body) ?? []).filter((i) => i.kind === 'question')) {
        const round = backlogItemRound(rounds, item, (r) => askedByRun(r.event.actor_label), (r, n) => unreportedBacklog(r).get(n) ?? null);
        round?.backlog.set(item.number, item.task ? { task: item.task } : { reason: item.reason ?? '' });
      }
      const found = findFactoryQuestions(event.body);
      if (found) {
        // SF-T-163: a newer round replaces the ones still open with nothing sent (the builder moved on).
        for (const round of rounds) if (!round.sent.size) round.answered = true;
        newest = { event, questions: found.questions, answered: false, reply: null, answers: null, words: null, sent: new Map(), proposals: new Map(), upstreamWords: null, backlog: new Map() };
        rounds.push(newest);
      }
      continue;
    }
    const marker = deferredMarker(event.payload);
    if (marker) {
      // Sending never closes a round; it marks what went up.
      const round = marker.kind === 'questions' ? rounds.find((r) => r.event.id === marker.event_id) : undefined;
      if (round && !round.answered) {
        for (const n of marker.numbers) if (n <= round.questions.questions.length) round.sent.set(n, event);
        for (const [n, p] of Object.entries(marker.proposals ?? {})) round.proposals.set(Number(n), p);
        if (marker.words) round.upstreamWords = marker.words;
      }
      continue;
    }
    if (isSendMessage(event.payload)) continue;
    const answers = parseFactoryAnswers(event.body);
    const words = answers ? null : parseQuestionsReply(event.body);
    // The round it answers: the one the factory's automatic Answers, or the card's Send answers or Reply in words, names.
    const named = auto?.event_id ?? (answers || words !== null ? answersForOf(event.payload) : null);
    const target = named ? rounds.find((r) => r.event.id === named) ?? null : newest;
    if (auto || named) {
      // Answers to one round (the factory's are written for the sender): only that round closes.
      if (target) target.answered = true;
    } else if (answers) {
      // Answers naming no round (written by hand) close every round.
      for (const round of rounds) round.answered = true;
    } else if (words !== null) {
      // A reply in words naming no round: the newest round's.
      if (target) target.answered = true;
    } else if (parseMentions(event.body).aiBuilder) {
      // SF-T-163: an @AI builder goes to the builder as the reply: the rounds with nothing sent close.
      // Any other comment is just a comment: it answers nothing.
      for (const round of rounds) if (!round.sent.size) round.answered = true;
    }
    if (target && !target.reply && (answers || words !== null)) {
      target.reply = event;
      target.answers = answers;
      target.words = words;
    }
  }
  return rounds;
}

/** The question numbers (1-based) a round can still send upstream: not sent yet, while the round is open. */
export function sendableQuestions(round: Pick<QuestionRound, 'answered' | 'questions' | 'sent'>): number[] {
  if (round.answered) return [];
  return round.questions.questions.map((_q, i) => i + 1).filter((n) => !round.sent.has(n));
}

/* Suggestions ----------------------------------------------------------- */

const RUN_LABEL_RE = /^Factory · R-(\d+)$/;

/** The run a factory message came from ("Factory · R-12"), or null. */
export function runNumberOfLabel(label: string | null | undefined): number | null {
  const match = label ? RUN_LABEL_RE.exec(label) : null;
  return match ? Number(match[1]) : null;
}

/** A decision on one suggestion, and the message that made it. */
export interface DecidedSuggestion<E extends RoundEvent = RoundEvent> {
  decision: SuggestionDecision;
  event: E;
}

/** One run's suggestions on a task (T-14), what people decided about each, and what went upstream (T-58). */
export interface SuggestionRound<E extends RoundEvent = RoundEvent> {
  /** The factory's message with the factory-suggestions block. */
  event: E;
  runNumber: number | null;
  suggestions: FactorySuggestions;
  /** By suggestion number (1-based); the first decision on each counts. */
  decided: Map<number, DecidedSuggestion<E>>;
  /** Suggestions out upstream, by number, with the message that sent each: sent, not decided yet, and the wait not removed. */
  sent: Map<number, E>;
  /** Jev's picks from the replies upstream, by suggestion number. */
  proposals: Map<number, UpstreamProposal>;
}

/**
 * Every run's suggestions in a task's events (oldest first), oldest round first. A decisions message
 * belongs to the round of the run it names ("Suggestions from R-12"); one naming no run, to the newest
 * round before it.
 */
export function suggestionRoundsOf<E extends RoundEvent>(events: E[]): SuggestionRound<E>[] {
  const rounds: SuggestionRound<E>[] = [];
  for (const event of events) {
    const ended = upstreamWaitEnded(event);
    if (ended) {
      // Taken back here (by hand, or the source is gone): the items are open to decide (or send) again.
      for (const round of rounds) {
        round.sent.clear();
        if (ended === 'unmirrored') round.proposals.clear();
      }
      continue;
    }
    if (event.kind !== 'chat' || !event.body) continue;
    if (event.actor_label) {
      // SF-T-308: the AI builder's report on Backlog picks it made (or didn't): onto those decisions.
      for (const item of (findFactoryBacklog(event.body) ?? []).filter((i) => i.kind === 'suggestion')) {
        const pending = (r: SuggestionRound<E>, n: number) => {
          const d = r.decided.get(n)?.decision;
          return d && backlogByAgent(d) && !d.outcome ? d.title : null;
        };
        const round = backlogItemRound(rounds, item, (r) => r.runNumber, pending);
        const decided = round?.decided.get(item.number);
        if (decided) decided.decision = { ...decided.decision, outcome: item.task ? { task: item.task } : { reason: item.reason ?? '' } };
      }
      const found = findFactorySuggestions(event.body);
      if (found) {
        rounds.push({
          event,
          runNumber: runNumberOfLabel(event.actor_label),
          suggestions: found.suggestions,
          decided: new Map(),
          sent: new Map(),
          proposals: new Map(),
        });
      }
      continue;
    }
    const marker = deferredMarker(event.payload);
    if (marker) {
      const round = marker.kind === 'suggestions' ? rounds.find((r) => r.event.id === marker.event_id) : undefined;
      if (round) {
        for (const n of marker.numbers) if (n <= round.suggestions.suggestions.length && !round.decided.has(n)) round.sent.set(n, event);
        for (const [n, p] of Object.entries(marker.proposals ?? {})) round.proposals.set(Number(n), p);
      }
      continue;
    }
    if (isSendMessage(event.payload)) continue;
    const parsed = parseSuggestionDecisions(event.body);
    if (!parsed) continue;
    // The same rule as Apply and the Inbox (SF-T-62): a message ref when its run posted more than one round.
    const round = roundForDecisions(rounds, parsed, (r) => r.runNumber, (r) => r.event.id);
    if (!round) continue;
    for (const decision of parsed.decisions) {
      if (!round.decided.has(decision.number)) round.decided.set(decision.number, { decision, event });
      round.sent.delete(decision.number);
    }
  }
  return rounds;
}

/** The suggestion numbers (1-based) a round can still send upstream: not decided and not out already. */
export function sendableSuggestions(round: Pick<SuggestionRound, 'decided' | 'sent' | 'suggestions'>): number[] {
  return round.suggestions.suggestions.map((_s, i) => i + 1).filter((n) => !round.decided.has(n) && !round.sent.has(n));
}

/* Open sent rounds (the worker's view) ------------------------------------ */

/** A round with items out upstream, still open. */
export type OpenSentRound<E extends RoundEvent = RoundEvent> =
  | { kind: 'questions'; round: QuestionRound<E> }
  | { kind: 'suggestions'; round: SuggestionRound<E> };

/** The task's rounds with items still out upstream (questions not answered, suggestions not decided). */
export function openSentRounds<E extends RoundEvent>(events: E[]): OpenSentRound<E>[] {
  return [
    ...questionRounds(events)
      .filter((r) => r.sent.size && !r.answered)
      .map((round) => ({ kind: 'questions' as const, round })),
    ...suggestionRoundsOf(events)
      .filter((r) => r.sent.size)
      .map((round) => ({ kind: 'suggestions' as const, round })),
  ];
}

/** When the round's items first went upstream (the oldest message that sent one). */
export function firstSentAt(round: { sent: Map<number, RoundEvent> }): string | null {
  const times = [...round.sent.values()].map((e) => e.created_at).sort();
  return times[0] ?? null;
}

/**
 * Whether a round can be submitted without a person: every open item went upstream and has a confident
 * option pick (Yes please, Backlog or No thanks for a suggestion), and nobody decided any of it here
 * since it was sent. Mixed rounds (items kept here), free-text questions and unmatched items never are.
 * (The worker also needs each pick's reply to be from someone trusted on the source.)
 */
export function roundFullyProposed(open: OpenSentRound): boolean {
  if (open.kind === 'questions') {
    const { round } = open;
    // SF-T-170: or the whole round went up and was answered there in words.
    const allSent = round.questions.questions.every((_q, i) => round.sent.has(i + 1));
    return allSent && (Boolean(round.upstreamWords) || round.questions.questions.every((_q, i) => round.proposals.has(i + 1)));
  }
  const { round } = open;
  const since = firstSentAt(round);
  const decidedSince = [...round.decided.values()].some((d) => since !== null && d.event.created_at > since);
  if (decidedSince) return false;
  const undecided = round.suggestions.suggestions.map((_s, i) => i + 1).filter((n) => !round.decided.has(n));
  return undecided.length > 0 && undecided.every((n) => round.sent.has(n) && isUpstreamSuggestionChoice(round.proposals.get(n)?.choice));
}

export function isUpstreamSuggestionChoice(value: unknown): value is UpstreamSuggestionChoice {
  return typeof value === 'string' && (UPSTREAM_SUGGESTION_CHOICES as readonly string[]).includes(value);
}

/**
 * Whether a person's comment leaves their own wait on the task (Deon, on SF-T-58): while a round has
 * items out upstream, an ordinary comment isn't their answer, so their wait stays. It ends as usual
 * when the comment answers (an Answers or suggestion decisions message), or @mentions the AI builder
 * (which picks the task up), or once nothing is out upstream. SF-T-163: the same holds while any round
 * of the builder's questions is open: a plain comment is just a comment, and only answering the round
 * (Send answers, Reply in words) or an @AI builder ends the wait and hands the task back.
 */
export function keepsOwnWait(input: {
  sentRoundOpen: boolean;
  /** SF-T-163: a round of questions was open before the message (questionRounds). */
  questionsOpen?: boolean;
  /** SF-T-163: the message closed a round of questions (Answers, a reply in words). */
  answersRound?: boolean;
  body: string;
  mentionsAiBuilder: boolean;
}): boolean {
  if (input.mentionsAiBuilder) return false;
  const decides = Boolean(parseSuggestionDecisions(input.body));
  if (input.sentRoundOpen && !parseFactoryAnswers(input.body) && !decides) return true;
  // SF-T-163: while the builder's questions are open, a plain comment isn't the answer to them.
  return Boolean(input.questionsOpen) && !input.answersRound && !decides;
}

/* What goes upstream ------------------------------------------------------ */

const LETTERS = 'abcdefghijklmnopqrstuvwxyz';
const oneLine = (s: string) => s.replace(/\s+/g, ' ').trim();

/** "a", "b", … for a question's options, as the text upstream lists them. */
export const optionLetter = (index: number) => LETTERS[index] ?? String(index + 1);

/**
 * The chosen questions as plain text for the source: each keeps its number from the round, with its
 * options lettered, and a closing line asks for replies by number. Never a factory block.
 */
export function formatUpstreamQuestions(questions: FactoryQuestion[], numbers: number[]): string {
  const lines = [numbers.length === 1 ? 'A question from the AI builder on this task:' : 'Questions from the AI builder on this task:', ''];
  for (const n of [...numbers].sort((a, b) => a - b)) {
    const q = questions[n - 1];
    if (!q) continue;
    lines.push(`${n}. ${q.header ? `${oneLine(q.header)}: ` : ''}${oneLine(q.question)}`);
    if (q.options.length) {
      q.options.forEach((o, i) => lines.push(`   ${optionLetter(i)}) ${oneLine(o.label)}${o.description ? ` (${oneLine(o.description)})` : ''}`));
      if (q.multiSelect) lines.push('   (pick one or more)');
    } else lines.push('   (answer in your own words)');
    lines.push('');
  }
  const [first] = [...numbers].sort((a, b) => a - b);
  const example = questions[first - 1]?.options.length ? `"${first}: a"` : `"${first}: …"`;
  lines.push(`Please reply with the number of each question and your answer, for example ${example}.`);
  return lines.join('\n');
}

/** The chosen suggestions as plain text for the source: numbers, titles and details, and how to reply. */
export function formatUpstreamSuggestions(suggestions: FactorySuggestion[], numbers: number[]): string {
  const lines = [numbers.length === 1 ? 'A suggestion from the AI builder on this task:' : 'Suggestions from the AI builder on this task:', ''];
  for (const n of [...numbers].sort((a, b) => a - b)) {
    const s = suggestions[n - 1];
    if (!s) continue;
    lines.push(`${n}. ${oneLine(s.title)}`);
    if (s.detail) lines.push(`   ${oneLine(s.detail)}`);
    lines.push('');
  }
  const [first] = [...numbers].sort((a, b) => a - b);
  lines.push(`Please reply with the number of each and "yes please", "backlog" (for later) or "no thanks", for example "${first}: yes please".`);
  return lines.join('\n');
}

/**
 * SF-T-170: the chosen questions for a source that reads blocks (jflo): a plain line, then just those
 * questions as a factory-questions block (numbered from 1 there), with `ask` naming who the send tags.
 */
export function formatUpstreamQuestionsBlock(questions: FactoryQuestion[], numbers: number[], ask: string | null): string {
  const chosen = [...numbers].sort((a, b) => a - b).flatMap((n) => (questions[n - 1] ? [questions[n - 1]] : []));
  const line = chosen.length === 1 ? 'A question from the AI builder on this task:' : 'Questions from the AI builder on this task:';
  return `${line}\n\n${formatFactoryQuestionsBlock({ ask, questions: chosen })}`;
}

/** SF-T-170: the chosen suggestions for a source that reads blocks: a plain line, then just those as a factory-suggestions block. */
export function formatUpstreamSuggestionsBlock(suggestions: FactorySuggestion[], numbers: number[]): string {
  const chosen = [...numbers].sort((a, b) => a - b).flatMap((n) => (suggestions[n - 1] ? [suggestions[n - 1]] : []));
  const line = chosen.length === 1 ? 'A suggestion from the AI builder on this task:' : 'Suggestions from the AI builder on this task:';
  return `${line}\n\n${formatFactorySuggestionsBlock({ suggestions: chosen })}`;
}

/* Exact replies from a source that reads blocks (SF-T-170) ------------------ */

/** A reply in one of the factory's own formats, as a source that reads blocks (jflo) writes it. */
export type ExactReply =
  | { kind: 'answers'; answers: (string | null)[] }
  | { kind: 'words'; text: string }
  | { kind: 'decisions'; ref: string | null; decisions: SuggestionDecision[] };

/** jflo names one of several suggestion rounds by the start of its comment's id: `**Suggestions · 3f9a1c2e**`. */
const REF_HEADER_RE = /^\*\*Suggestions · ([0-9a-f]{8})\*\*$/;

/** The reply read exactly (Answers, Reply to the questions, Suggestions decisions); null for anything else, which Jev reads. */
export function parseExactReply(body: string): ExactReply | null {
  const answers = parseFactoryAnswers(body);
  // A skipped question (SF-T-227) is no answer here: nothing to pick from it.
  if (answers) return { kind: 'answers', answers: answers.map((a) => (isSkippedAnswer(a) ? null : a)) };
  const words = parseQuestionsReply(body);
  if (words) return { kind: 'words', text: words };
  const lines = body.replace(/\r\n?/g, '\n').trim().split('\n');
  const ref = REF_HEADER_RE.exec(lines[0]?.trim() ?? '')?.[1] ?? null;
  // Read with the factory's own parser, as the plain header (the ref only says which comment it answers).
  const parsed = parseSuggestionDecisions(ref ? ['**Suggestions**', ...lines.slice(1)].join('\n') : body);
  // A run named (the factory's own header) isn't jflo's: left to Jev.
  if (!parsed || parsed.runNumber !== null) return null;
  // jflo offers only Yes please, Backlog and No thanks.
  const decisions = parsed.decisions.filter((d) => isUpstreamSuggestionChoice(d.action));
  return decisions.length ? { kind: 'decisions', ref, decisions } : null;
}

/** A message that sent items up as blocks, still with items out: what an exact reply can answer. */
export interface BlocksSend {
  id: string;
  kind: DeferredKind;
  /** The round's numbers it carried, in the order the source numbers them (1, 2, … there). */
  numbers: number[];
  /** When it went up there. */
  postedAt: string;
  /** suggestionsMessageRef of its comment's id upstream; null when it has none. */
  ref: string | null;
}

/** When a sending message went up there (`deferred.posted_at`), or was written here. */
export function sendPostedAt(message: RoundEvent): string {
  const d = isRecord(message.payload) && isRecord(message.payload.deferred) ? message.payload.deferred : null;
  const posted = typeof d?.posted_at === 'string' ? Date.parse(d.posted_at.replace(' ', 'T')) : NaN;
  const written = Date.parse(message.created_at);
  return new Date(Number.isFinite(posted) && posted > written ? posted : written).toISOString();
}

/** The upstream comment id's ref for a decisions header (suggestionsMessageRef), or null. */
export const upstreamRef = (externalId: string | null | undefined) => (externalId && !externalId.startsWith('pending:') ? suggestionsMessageRef(externalId) : null);

/**
 * Which send an exact reply answers, among the open blocks sends. Answers: the newest questions send
 * before it with as many questions as the reply has answers. A reply in words: the newest questions send
 * before it. Decisions: the send its ref names, or without one the newest suggestions send before it.
 * Null: none fits, and Jev reads it.
 */
export function exactReplyTarget(reply: ExactReply, replyAt: string, sends: BlocksSend[]): BlocksSend | null {
  // To the whole second, as repliesSince: a source gives a comment's time in seconds.
  const at = new Date(Math.floor(Date.parse(replyAt) / 1000) * 1000 + 999).toISOString();
  const before = sends.filter((s) => s.postedAt <= at).sort((a, b) => a.postedAt.localeCompare(b.postedAt));
  if (reply.kind === 'decisions') {
    const ofKind = before.filter((s) => s.kind === 'suggestions');
    if (reply.ref) return ofKind.find((s) => s.ref === reply.ref) ?? null;
    return ofKind[ofKind.length - 1] ?? null;
  }
  const ofKind = before.filter((s) => s.kind === 'questions' && (reply.kind === 'words' || s.numbers.length === reply.answers.length));
  return ofKind[ofKind.length - 1] ?? null;
}

/**
 * What an exact reply says for its send's items, by their number in the round (mapped back through the
 * send's numbers: the source numbered them from 1). Only items in `out` (still out) count. Questions:
 * each answer as given (a multi-select one split into its picks); suggestions: Yes please, Backlog or No thanks.
 */
export function exactPicks(
  reply: ExactReply,
  send: Pick<BlocksSend, 'numbers'>,
  out: number[],
  questions: FactoryQuestion[],
  from: { reply_event_id: string; reply_author: string | null }
): { picks: Map<number, UpstreamProposal>; words: UpstreamWords | null } {
  const picks = new Map<number, UpstreamProposal>();
  if (reply.kind === 'words') return { picks, words: { text: reply.text, ...from } };
  const pick = (n: number, choice: string, choices?: string[]) => {
    if (out.includes(n)) picks.set(n, { choice, ...(choices ? { choices } : {}), confidence: 1, ...from });
  };
  if (reply.kind === 'answers') {
    reply.answers.forEach((answer, i) => {
      const n = send.numbers[i];
      if (!answer || n === undefined) return;
      const q = questions[n - 1];
      if (!q) return;
      if (q.multiSelect) {
        const choices = answer.split(';').map((c) => c.trim()).filter(Boolean);
        // The answer as written (SF-T-224): words of their own after the options keep their semicolons.
        pick(n, answer.trim(), choices);
      } else pick(n, answer);
    });
  } else {
    for (const d of reply.decisions) {
      const n = send.numbers[d.number - 1];
      if (n !== undefined) pick(n, d.action);
    }
  }
  return { picks, words: null };
}

/**
 * SF-T-224: a question's answer from a pick read upstream, as the card's answer. The options it starts with,
 * each a whole label followed by ";" or the end (formatFactoryAnswers writes picks before the rest), are
 * picked, one at most for a single-select question. A label with a ";" of its own still matches whole, the
 * longest first. Whatever follows (a reply in someone's own words, like jflo's "or write your own answer")
 * goes in `other` as it was written, its own semicolons and all. Undefined when there's nothing.
 */
export function proposalAnswer(question: FactoryQuestion, proposal: UpstreamProposal | undefined): FactoryAnswer | undefined {
  if (!proposal) return undefined;
  const labels = question.options.map((o) => o.label).sort((x, y) => y.length - x.length);
  const picked: string[] = [];
  let rest = proposal.choice.trim().replace(/^[;\s]+/, '');
  while (rest && (question.multiSelect || !picked.length)) {
    const label = labels.find((l) => !picked.includes(l) && rest.startsWith(l) && /^\s*(;|$)/.test(rest.slice(l.length)));
    if (!label) break;
    picked.push(label);
    rest = rest.slice(label.length).replace(/^[;\s]+/, '');
  }
  return picked.length || rest ? { picked, other: rest } : undefined;
}

/**
 * SF-T-224: the note's text for a reply with words of its own to a question with options, so it's clear
 * why they're in the box and not ticked. Undefined for options alone: the note shows them as they are.
 */
export function ownWordsNote(answer: FactoryAnswer | undefined): string | undefined {
  if (!answer?.other) return undefined;
  const words = `“${answer.other.replace(/\s+/g, ' ')}”`;
  return answer.picked.length ? `${answer.picked.join('; ')}, and in their own words: ${words}` : `In their own words: ${words}`;
}

/**
 * The line under the factory's automatic answers (`verb`: Answered for questions, Decided for
 * suggestions). It names who replied: only people on the source's trusted list (by their account id
 * there, which a rename doesn't change) answer by themselves.
 */
export function upstreamAnswersNote(sourceLabel: string, authors: Array<string | null> = [], verb: 'Answered' | 'Decided' = 'Answered'): string {
  const names = [...new Set(authors.map((a) => a?.trim()).filter((a): a is string => Boolean(a)))];
  const by = names.length ? ` by ${names.join(', ')}` : '';
  return `_${verb} from the ${sourceLabel} reply${by}, trusted on this source by their ${sourceLabel} account._`;
}

/** Who wrote the replies Jev's picks came from, for the automatic answers' note and the log. */
export function proposalAuthors(proposals: ReadonlyMap<number, UpstreamProposal>): string[] {
  return [...new Set([...proposals.values()].map((p) => p.reply_author?.trim() || 'someone unnamed'))];
}

/** The items still out upstream, as a line for the AI builder's run ("" when none). */
export function outUpstreamNote(open: OpenSentRound[], sourceLabel: string): string {
  const parts = open.map((o) => {
    const numbers = [...o.round.sent.keys()].sort((a, b) => a - b).join(', ');
    const from = runNumberOfLabel(o.round.event.actor_label);
    const what = o.kind === 'questions' ? (o.round.sent.size === 1 ? 'question' : 'questions') : o.round.sent.size === 1 ? 'suggestion' : 'suggestions';
    return `${what} ${numbers}${from !== null ? ` from R-${from}` : ''}`;
  });
  if (!parts.length) return '';
  return `Still out with ${sourceLabel}, waiting for a reply there: ${parts.join('; ')}. Don't answer or ask these again; the answers come back on the task.`;
}
